import { Fragment, ReactNode, createContext, memo, useContext, useMemo } from 'react'

import { faArrowRight, faNetworkWired } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import numeral from 'numeral'

import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Card } from '@infura/packages.ui/Card'
import { Codeblock } from '@infura/packages.ui/Codeblock'
import { Heading } from '@infura/packages.ui/Heading'
import { SemanticIcon } from '@infura/packages.ui/SemanticIcon'
import { Tag } from '@infura/packages.ui/Tag'
import { Text } from '@infura/packages.ui/Text'
import { Tooltip } from '@infura/packages.ui/Tooltip'
import { V1Subnet } from '@infura/packages.uks'

import { ENDPOINT_PROTOCOL, EndpointProtocol } from 'lib/utils/keys'

export interface NetworkCardProps {
  id?: string
  activeProtocol: EndpointProtocol
  children?: ReactNode | Array<ReactNode>
  endpointUrl?: string
  keyId: string
  network: string
  subnets: { [subnetKey: string]: V1Subnet }
  unsupportedProtocol?: boolean
  className?: string
  requests?: number
  subnet?: V1Subnet
  dataTestId?: string
  docsUrl: string
  iconUrl: string
  alias: string
  dinEnabled: boolean
}

const NetworkCardContext = createContext<
  NetworkCardProps & {
    subnet: V1Subnet
  }
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
>(null)

const NetworkCard = ({
  activeProtocol,
  alias,
  children,
  className = '',
  dataTestId,
  dinEnabled,
  docsUrl,
  endpointUrl,
  iconUrl,
  id,
  keyId,
  network,
  requests,
  subnet,
  subnets,
  unsupportedProtocol
}: NetworkCardProps) => {
  const contextValue = useMemo(
    () => ({
      activeProtocol,
      alias,
      dinEnabled,
      docsUrl,
      endpointUrl,
      iconUrl,
      keyId,
      network,
      requests,
      subnet,
      subnets
    }),
    [activeProtocol, docsUrl, endpointUrl, keyId, network, requests, subnet, subnets, iconUrl, alias, dinEnabled]
  )

  const classNames = clsx(
    'flex flex-wrap items-center gap-y-4 md:gap-y-3 !rounded-none md:flex-nowrap md:gap-0 border-l-0 border-r-0',
    {
      'bg-background-alternative opacity-60': unsupportedProtocol
    },
    className
  )

  return (
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    <NetworkCardContext.Provider value={contextValue}>
      <Card className={classNames} dataTestId={dataTestId} id={id} key={`network-card-${network}`}>
        <Fragment>
          {children ? (
            children
          ) : (
            <>
              <NetworkCard.Icon />
              <NetworkCard.Heading />
              <NetworkCard.DocsHref />
              {!unsupportedProtocol ? <NetworkCard.Requests /> : null}
              {!unsupportedProtocol ? <NetworkCard.Endpoint /> : null}
              {unsupportedProtocol ? <NetworkCard.UnsupportedProtocol /> : null}
            </>
          )}
        </Fragment>
      </Card>
    </NetworkCardContext.Provider>
  )
}

NetworkCard.Requests = memo(function KeyCardRequests({
  className = '',
  dataTestId = ''
}: {
  className?: string
  dataTestId?: string
}) {
  const { requests } = useContext(NetworkCardContext)

  if (!!requests)
    return (
      <div className={clsx('mx-4', className)} data-testid={`${dataTestId}-requests`}>
        <Tag className='md:ml-auto' theme='success'>
          {numeral(requests).format()} requests in 1 hour
        </Tag>
      </div>
    )
  else
    return (
      <div className={clsx('mx-4', className)} data-testid={`${dataTestId}-no-requests`}>
        <Tag className='md:ml-auto' theme='warning'>
          No requests in 1 hour
        </Tag>
      </div>
    )
})

// The values returned from the key service refer to a non-publicly accessible s3 bucket.
// Which means we have to construct a valid URL using our cloudfront distribution.
//
// Ideally we'd have the API return using this baseurl
export const baseUrl = `https://df1du3ux0hbg2.cloudfront.net`
NetworkCard.Icon = memo(function KeyCardIcon() {
  const { alias, iconUrl, network } = useContext(NetworkCardContext)

  let imgSrc: null | string = null
  try {
    const url = new URL(iconUrl)
    imgSrc = `${baseUrl}${url.pathname}`
  } catch (e) {
    // If the URL can't be parsed we'll use the fallback
  }

  return (
    <div className='mr-3 h-6 w-6'>
      {imgSrc ? (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img alt={alias ?? network} className='h-6 w-auto' height='24' src={imgSrc} width='auto' />
      ) : (
        <SemanticIcon className='h-6 w-6' icon={faNetworkWired} />
      )}
    </div>
  )
})

NetworkCard.Heading = memo(function KeyCardHeading({ className = '' }: { className?: string }) {
  const { alias, network } = useContext(NetworkCardContext)
  const classNames = clsx('text-sm md:text-base flex-grow', className)
  return <Heading className={classNames}>{alias || network}</Heading>
})

NetworkCard.SubHeading = memo(function KeyCardSubHeading({
  className = '',
  text = ''
}: {
  className?: string
  text?: string
}) {
  const classNames = clsx('text-xs md:text-base flex-grow leading-none', className)
  return (
    <Text className={classNames} theme='muted'>
      {text}
    </Text>
  )
})

NetworkCard.DocsHref = memo(function KeyCardDocsHref({ className = '' }: { className?: string }) {
  const { docsUrl, network } = useContext(NetworkCardContext)
  return (
    <ActionLink
      className={clsx('mt-1.25 ml-0 mr-2 inline-block items-center font-medium', className)}
      dataTestId='active-endpoints-table-row-docs-url'
      href={docsUrl}
      hrefTarget='_blank'
      iconPosition='left'
      onClick={() =>
        trackClickForSegmentAnalytics({
          clickText: 'Docs',
          clickUrl: docsUrl,
          location: network,
          type: 'Text'
        })
      }
    >
      Docs
    </ActionLink>
  )
})

NetworkCard.DinLabel = memo(function KeyCardDinLabel() {
  const { dinEnabled } = useContext(NetworkCardContext)
  return dinEnabled ? (
    <div className='mr-auto flex'>
      <Tooltip
        content={
          <div className='flex flex-col'>
            <span className='mb-2'>RPC calls to the network are routed to partner infrastructure providers.</span>
            <span className='mb-4 text-xs'>*Performance constraints may apply.</span>
            <ActionLink
              className='text-primary-default'
              href='https://www.infura.io/solutions/decentralized-infrastructure-service'
              hrefTarget='_blank'
              icon={faArrowRight}
            >
              Dive Deeper Into DIN
            </ActionLink>
          </div>
        }
        placement='top'
      >
        <Tag data-testid='din-badge'>
          <span>Decentralized by</span>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt='DIN, Logo' className='ml-2' src='/images/icons/icon-din.svg' />
        </Tag>
      </Tooltip>
    </div>
  ) : null
})

NetworkCard.Endpoint = memo(function KeyCardKey({
  className = '',
  urlOverride = ''
}: {
  className?: string
  urlOverride?: string
}) {
  const { activeProtocol, endpointUrl, keyId, subnet: activeSubnet } = useContext(NetworkCardContext)
  const isHttps = activeProtocol === ENDPOINT_PROTOCOL.HTTPS
  const protocolPrefix = isHttps ? 'https://' : 'wss://'

  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  const subnetScheme = activeSubnet?.schemes[activeProtocol]

  const version = subnetScheme?.version
  const subnetUrl = subnetScheme?.url

  const endpoint = urlOverride || endpointUrl || `${protocolPrefix}${subnetUrl}/${version}/${keyId}`
  const classes = clsx('w-full md:w-7/12', className)

  return (
    <div className={classes}>
      <Codeblock buttonPlacement='left' className='mt-4 text-xs sm:text-sm md:text-base' type='single-line'>
        {endpoint}
      </Codeblock>
    </div>
  )
})

NetworkCard.UnsupportedProtocol = memo(function KeyCardUnsupportedProtocol({
  className = ''
}: React.HTMLAttributes<'h1'>) {
  const { activeProtocol } = useContext(NetworkCardContext)
  const classes = clsx('w-full', className)

  const protocolName = activeProtocol === 'wss' ? 'websocket' : 'https'

  const text = `This network does not currently offer ${protocolName} endpoints.`
  return (
    <Codeblock className={classes} hasCopyButton={false} hidden={false} type='single-line'>
      {text}
    </Codeblock>
  )
})

export default NetworkCard
